import { createRouter, createWebHistory } from 'vue-router'
import { previousRoute } from '@/router/history'
import { workspaceRoutes } from '@/router/workspaceRoutes.js'
import { entityRoutes, legacyEntityFieldRoutes, appEntityRoutes, appEntityNotFoundRoute } from '@/router/entityRoutes.js'
import { entityRouteForDefinition, findAppEntityDefinition, isEntityRoute, missingAppEntityRoute } from '@/router/appEntityRouting.js'
import VueCookies from 'vue-cookies'
import {
    cachedTenantActiveModules,
    normalizeTenantModuleKey,
    rememberTenantActiveModules,
    rememberTenantOfferSetup,
} from '@/composables/useTenantModules.js'

const ROUTE_TENANT_MODULES_QUERY = `
    query RouteTenantModules {
        currentTenant {
            active_modules
            offer_setup_required
            internal_chat_enabled
            apps_enabled
        }
    }
`

export const router = createRouter({
    history: createWebHistory('/'),
    routes: [
        // Global Routes (without tenant)
        {
            path: '/login',
            name: 'login',
            component: () => import('@/pages/Login.vue')
        },

        {
            path: '/auth/callback',
            name: 'auth.callback',
            component: () => import('@/pages/AuthCallback.vue')
        },
        {
            path: '/setup',
            name: 'admin.setup.global',
            component: () => import('@/pages/admin/Setup.vue'),
            meta: { layoutless: true }
        },
        ...workspaceRoutes,
        {
            path: '/:tenantId/apps',
            name: 'apps.index',
            component: () => import('@/pages/admin/app-catalog/Index.vue'),
        },
        {
            path: '/:tenantId/apps/install',
            name: 'apps.catalog',
            component: () => import('@/pages/admin/app-catalog/Catalog.vue'),
        },
        {
            path: '/:tenantId/apps/install/:appKey',
            name: 'apps.install',
            component: () => import('@/pages/admin/app-builder/Install.vue'),
        },
        {
            path: '/:tenantId/apps/:installationKey/builder',
            name: 'apps.builder',
            component: () => import('@/pages/admin/app-builder/Index.vue'),
        },
        {
            path: '/:tenantId/apps/:installationKey',
            name: 'apps.show',
            component: () => import('@/pages/admin/app-catalog/Show.vue'),
        },
        {
            path: '/auth/email/verify/:token',
            name: 'public.auth.email.verify',
            component: () => import('@/pages/public/auth/AdminEmailVerification.vue'),
            meta: { public: true, layoutless: true }
        },
        {
            path: '/public/payment-requests/:token',
            name: 'public.payment-request.show',
            component: () => import('@/pages/public/payment-request/Show.vue'),
            meta: { public: true, layoutless: true }
        },
        {
            path: '/public/quote-demo',
            name: 'public.quote-demo',
            component: () => import('@/pages/public/quote-demo/Index.vue'),
            meta: { public: true, layoutless: true }
        },
        {
            path: '/public/quote/approval/:token',
            name: 'public.quote.approval',
            component: () => import('@/pages/public/quote/ApprovalConfirmed.vue'),
            meta: { public: true, layoutless: true }
        },
        {
            path: '/public/quote/:token/download/pdf',
            name: 'public.quote.download',
            component: () => import('@/pages/public/quote/Download.vue'),
            meta: { public: true, layoutless: true }
        },
        {
            path: '/public/quote/:token',
            name: 'public.quote.show',
            component: () => import('@/pages/public/quote/Show.vue'),
            meta: { public: true, layoutless: true }
        },

        // Dashboard / Home (redirect to products with tenant)
        {
            path: '/',
            redirect: '/login'
        },
        {
            path: '/:tenantId',
            redirect: to => `/${to.params.tenantId}/dashboard`
        },

        {
            path: '/:tenantId/dashboard',
            name: 'dashboard.index',
            component: () => import('@/pages/dashboard/Index.vue')
        },

        {
            path: '/:tenantId/setup',
            name: 'admin.setup',
            component: () => import('@/pages/admin/Setup.vue'),
            meta: { layoutless: true }
        },

        {
            path: '/:tenantId/trial-expired',
            name: 'admin.trial-expired',
            component: () => import('@/pages/admin/TrialExpired.vue'),
            meta: { layoutless: true }
        },
        {
            path: '/:tenantId/module-unavailable',
            name: 'admin.module-unavailable',
            component: () => import('@/pages/admin/ModuleUnavailable.vue'),
            meta: { layoutless: true }
        },

        // Profile
        {
            path: '/:tenantId/profile',
            name: 'profile',
            component: () => import('@/pages/admin/Profile.vue')
        },
        {
            path: '/:tenantId/businesses',
            name: 'business.index',
            component: () => import('@/pages/admin/business/Index.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/businesses/create',
            name: 'business.create',
            component: () => import('@/pages/admin/business/Edit.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/businesses/:id/edit',
            name: 'business.edit',
            component: () => import('@/pages/admin/business/Edit.vue'),
            meta: { module: 'quote' }
        },

        // Import Admin
        {
            path: '/:tenantId/import-admin',
            name: 'import-admin',
            component: () => import('@/pages/admin/import-admin/Index.vue')
        },

        // Email Log
        {
            path: '/:tenantId/email-log',
            name: 'email-log',
            component: () => import('@/pages/admin/email-log/Index.vue')
        },

        // Media Library (redirect alle rotte entity generiche)
        {
            path: '/:tenantId/media-library',
            name: 'media-library.index',
            redirect: to => ({ name: 'entity.index', params: { tenantId: to.params.tenantId, entityType: 'media' } })
        },
        {
            path: '/:tenantId/media-library/create',
            name: 'media-library.create',
            redirect: to => ({ name: 'entity.create', params: { tenantId: to.params.tenantId, entityType: 'media' } })
        },
        {
            path: '/:tenantId/media-library/:id/edit',
            name: 'media-library.edit',
            redirect: to => ({ name: 'entity.edit', params: { tenantId: to.params.tenantId, entityType: 'media', id: to.params.id } })
        },

        // Quote
        {
            path: '/:tenantId/quotes',
            name: 'quote.index',
            component: () => import('@/pages/admin/quote/Index.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/requests',
            name: 'quote.sales-request.index',
            component: () => import('@/pages/admin/quote/sales-request/Index.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/requests/create',
            name: 'quote.sales-request.create',
            component: () => import('@/pages/admin/quote/sales-request/Create.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/requests/:id/edit',
            name: 'quote.sales-request.edit',
            component: () => import('@/pages/admin/quote/sales-request/Edit.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/quotes',
            name: 'quote.quote.index',
            component: () => import('@/pages/admin/quote/quote/Index.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/quotes/create',
            name: 'quote.quote.create',
            component: () => import('@/pages/admin/quote/quote/Create.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/quotes/settings',
            name: 'quote.quote.settings',
            redirect: to => ({ name: 'business.index', params: { tenantId: to.params.tenantId } }),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/quotes/:id/edit',
            name: 'quote.quote.edit',
            component: () => import('@/pages/admin/quote/quote/Edit.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/quotes/:id/conversion-plan',
            name: 'quote.quote.conversion-plan',
            component: () => import('@/pages/admin/quote/quote/ConversionPlan.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/accounts',
            name: 'quote.account.index',
            component: () => import('@/pages/admin/quote/account/Index.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/accounts/create',
            name: 'quote.account.create',
            component: () => import('@/pages/admin/quote/account/Create.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/accounts/:id/edit',
            name: 'quote.account.edit',
            component: () => import('@/pages/admin/quote/account/Edit.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/people',
            name: 'quote.person.index',
            component: () => import('@/pages/admin/quote/person/Index.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/people/create',
            name: 'quote.person.create',
            component: () => import('@/pages/admin/quote/person/Create.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/people/:id/edit',
            name: 'quote.person.edit',
            component: () => import('@/pages/admin/quote/person/Edit.vue'),
            meta: { module: 'quote' }
        },
        {
            path: '/:tenantId/quote/follow-ups',
            name: 'quote.follow-up.index',
            component: () => import('@/pages/admin/quote/Placeholder.vue'),
            meta: { module: 'quote', quotePage: 'followUps' }
        },

        // Raccolta problemi
        {
            path: '/:tenantId/problem-discovery/families',
            name: 'problem-discovery.family.index',
            component: () => import('@/pages/admin/problem-discovery/family/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/families/create',
            name: 'problem-discovery.family.create',
            component: () => import('@/pages/admin/problem-discovery/family/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/families/:id/edit',
            name: 'problem-discovery.family.edit',
            component: () => import('@/pages/admin/problem-discovery/family/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/problems',
            name: 'problem-discovery.problem.index',
            component: () => import('@/pages/admin/problem-discovery/problem/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/problems/create',
            name: 'problem-discovery.problem.create',
            component: () => import('@/pages/admin/problem-discovery/problem/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/problems/:id/edit',
            name: 'problem-discovery.problem.edit',
            component: () => import('@/pages/admin/problem-discovery/problem/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/customer-profiles',
            name: 'problem-discovery.customer-profile.index',
            component: () => import('@/pages/admin/problem-discovery/customer-profile/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/customer-profiles/create',
            name: 'problem-discovery.customer-profile.create',
            component: () => import('@/pages/admin/problem-discovery/customer-profile/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/customer-profiles/:id/edit',
            name: 'problem-discovery.customer-profile.edit',
            component: () => import('@/pages/admin/problem-discovery/customer-profile/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/feedback',
            name: 'problem-discovery.feedback.index',
            component: () => import('@/pages/admin/problem-discovery/feedback/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/feedback/create',
            name: 'problem-discovery.feedback.create',
            component: () => import('@/pages/admin/problem-discovery/feedback/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/feedback/:id/edit',
            name: 'problem-discovery.feedback.edit',
            component: () => import('@/pages/admin/problem-discovery/feedback/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/sources',
            name: 'problem-discovery.source.index',
            component: () => import('@/pages/admin/problem-discovery/source/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/sources/create',
            name: 'problem-discovery.source.create',
            component: () => import('@/pages/admin/problem-discovery/source/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/sources/:id/edit',
            name: 'problem-discovery.source.edit',
            component: () => import('@/pages/admin/problem-discovery/source/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/source-pages',
            name: 'problem-discovery.source-page.index',
            component: () => import('@/pages/admin/problem-discovery/source-page/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/source-pages/create',
            name: 'problem-discovery.source-page.create',
            component: () => import('@/pages/admin/problem-discovery/source-page/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/source-pages/:id/edit',
            name: 'problem-discovery.source-page.edit',
            component: () => import('@/pages/admin/problem-discovery/source-page/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/evidence',
            name: 'problem-discovery.evidence.index',
            component: () => import('@/pages/admin/problem-discovery/evidence/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/evidence/create',
            name: 'problem-discovery.evidence.create',
            component: () => import('@/pages/admin/problem-discovery/evidence/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/evidence/:id/edit',
            name: 'problem-discovery.evidence.edit',
            component: () => import('@/pages/admin/problem-discovery/evidence/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/players',
            name: 'problem-discovery.player.index',
            component: () => import('@/pages/admin/problem-discovery/player/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/players/create',
            name: 'problem-discovery.player.create',
            component: () => import('@/pages/admin/problem-discovery/player/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/players/:id/edit',
            name: 'problem-discovery.player.edit',
            component: () => import('@/pages/admin/problem-discovery/player/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/market-solutions',
            name: 'problem-discovery.market-solution.index',
            component: () => import('@/pages/admin/problem-discovery/market-solution/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/market-solutions/create',
            name: 'problem-discovery.market-solution.create',
            component: () => import('@/pages/admin/problem-discovery/market-solution/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/market-solutions/:id/edit',
            name: 'problem-discovery.market-solution.edit',
            component: () => import('@/pages/admin/problem-discovery/market-solution/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/solutions',
            name: 'problem-discovery.solution.index',
            component: () => import('@/pages/admin/problem-discovery/solution/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/solutions/create',
            name: 'problem-discovery.solution.create',
            component: () => import('@/pages/admin/problem-discovery/solution/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/solutions/:id/edit',
            name: 'problem-discovery.solution.edit',
            component: () => import('@/pages/admin/problem-discovery/solution/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/assets',
            name: 'problem-discovery.asset.index',
            component: () => import('@/pages/admin/problem-discovery/asset/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/assets/create',
            name: 'problem-discovery.asset.create',
            component: () => import('@/pages/admin/problem-discovery/asset/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/assets/:id/edit',
            name: 'problem-discovery.asset.edit',
            component: () => import('@/pages/admin/problem-discovery/asset/Edit.vue')
        },
        {
            path: '/:tenantId/problem-discovery/solution-asset-requirements',
            name: 'problem-discovery.solution-asset-requirement.index',
            component: () => import('@/pages/admin/problem-discovery/solution-asset-requirement/Index.vue')
        },
        {
            path: '/:tenantId/problem-discovery/solution-asset-requirements/create',
            name: 'problem-discovery.solution-asset-requirement.create',
            component: () => import('@/pages/admin/problem-discovery/solution-asset-requirement/Create.vue')
        },
        {
            path: '/:tenantId/problem-discovery/solution-asset-requirements/:id/edit',
            name: 'problem-discovery.solution-asset-requirement.edit',
            component: () => import('@/pages/admin/problem-discovery/solution-asset-requirement/Edit.vue')
        },

        // Expense Manager
        {
            path: '/:tenantId/expense-manager/accounts',
            name: 'expense-manager.account.index',
            component: () => import('@/pages/admin/expense-manager/account/Index.vue')
        },
        {
            path: '/:tenantId/expense-manager/accounts/create',
            name: 'expense-manager.account.create',
            component: () => import('@/pages/admin/expense-manager/account/Create.vue')
        },
        {
            path: '/:tenantId/expense-manager/accounts/:id/edit',
            name: 'expense-manager.account.edit',
            component: () => import('@/pages/admin/expense-manager/account/Edit.vue')
        },
        {
            path: '/:tenantId/expense-manager/categories',
            name: 'expense-manager.category.index',
            component: () => import('@/pages/admin/expense-manager/category/Index.vue')
        },
        {
            path: '/:tenantId/expense-manager/categories/create',
            name: 'expense-manager.category.create',
            component: () => import('@/pages/admin/expense-manager/category/Create.vue')
        },
        {
            path: '/:tenantId/expense-manager/categories/:id/edit',
            name: 'expense-manager.category.edit',
            component: () => import('@/pages/admin/expense-manager/category/Edit.vue')
        },
        {
            path: '/:tenantId/expense-manager/category-rules',
            name: 'expense-manager.category-rule.index',
            component: () => import('@/pages/admin/expense-manager/category-rule/Index.vue')
        },
        {
            path: '/:tenantId/expense-manager/category-rules/create',
            name: 'expense-manager.category-rule.create',
            component: () => import('@/pages/admin/expense-manager/category-rule/Create.vue')
        },
        {
            path: '/:tenantId/expense-manager/category-rules/:id/edit',
            name: 'expense-manager.category-rule.edit',
            component: () => import('@/pages/admin/expense-manager/category-rule/Edit.vue')
        },
        {
            path: '/:tenantId/expense-manager/transactions',
            name: 'expense-manager.transaction.index',
            component: () => import('@/pages/admin/expense-manager/transaction/Index.vue')
        },
        {
            path: '/:tenantId/expense-manager/runway',
            name: 'expense-manager.runway.index',
            component: () => import('@/pages/admin/expense-manager/runway/Index.vue')
        },
        {
            path: '/:tenantId/expense-manager/forecast-flows',
            name: 'expense-manager.forecast-flow.index',
            component: () => import('@/pages/admin/expense-manager/forecast-flow/Index.vue')
        },
        {
            path: '/:tenantId/expense-manager/forecast-occurrences',
            name: 'expense-manager.forecast-occurrence.index',
            component: () => import('@/pages/admin/expense-manager/forecast-occurrence/Index.vue')
        },
        {
            path: '/:tenantId/expense-manager/import',
            name: 'expense-manager.import',
            component: () => import('@/pages/admin/expense-manager/Import.vue')
        },

        // Reception
        {
            path: '/:tenantId/reception',
            name: 'reception.index',
            component: () => import('@/pages/admin/reception/Index.vue')
        },
        {
            path: '/:tenantId/reception/calls',
            name: 'reception.call.index',
            component: () => import('@/pages/admin/reception/call/Index.vue')
        },
        {
            path: '/:tenantId/reception/calls/:id/edit',
            name: 'reception.call.edit',
            component: () => import('@/pages/admin/reception/call/Edit.vue')
        },

        // Stats
        {
            path: '/:tenantId/stats/sales',
            name: 'stats.sales',
            component: () => import('@/pages/admin/stats/SaleStats.vue')
        },
        {
            path: '/:tenantId/stats/quotes',
            name: 'stats.quotes',
            component: () => import('@/pages/admin/stats/QuoteStats.vue')
        },

        // Discount
        {
            path: '/:tenantId/discounts',
            name: 'discount.index',
            component: () => import('@/pages/admin/discount/Index.vue')
        },
        {
            path: '/:tenantId/discounts/create',
            name: 'discount.create',
            component: () => import('@/pages/admin/discount/Create.vue')
        },
        {
            path: '/:tenantId/discounts/:id/edit',
            name: 'discount.edit',
            component: () => import('@/pages/admin/discount/Edit.vue')
        },

        // Discount group
        {
            path: '/:tenantId/discount-groups',
            name: 'discount-group.index',
            component: () => import('@/pages/admin/discount-group/Index.vue')
        },
        {
            path: '/:tenantId/discount-groups/create',
            name: 'discount-group.create',
            component: () => import('@/pages/admin/discount-group/Create.vue')
        },
        {
            path: '/:tenantId/discount-groups/:id/edit',
            name: 'discount-group.edit',
            component: () => import('@/pages/admin/discount-group/Edit.vue')
        },

        // Configuration
        {
            path: '/:tenantId/configuration',
            name: 'configuration',
            component: () => import('@/pages/admin/configuration/Index.vue')
        },
        {
            path: '/:tenantId/settings/billing',
            name: 'settings.billing',
            component: () => import('@/pages/admin/BillingSettings.vue'),
            meta: { allowWhenUpgradeRequired: true }
        },
        {
            path: '/:tenantId/settings/billing/plans/:productKey?',
            name: 'settings.billing.plans',
            component: () => import('@/pages/admin/BillingPlans.vue'),
            meta: { allowWhenUpgradeRequired: true }
        },

        {
            path: '/:tenantId/configuration/users',
            name: 'configuration.user.index',
            component: () => import('@/pages/admin/user/Index.vue')
        },

        {
            path: '/:tenantId/configuration/users/create',
            name: 'configuration.user.create',
            component: () => import('@/pages/admin/user/Create.vue')
        },

        {
            path: '/:tenantId/configuration/users/:id/edit',
            name: 'configuration.user.edit',
            component: () => import('@/pages/admin/user/Edit.vue')
        },
        {
            path: '/:tenantId/configuration/profiles',
            name: 'configuration.profile.index',
            component: () => import('@/pages/admin/tenant-role/Index.vue')
        },
        {
            path: '/:tenantId/configuration/profiles/create',
            name: 'configuration.profile.create',
            component: () => import('@/pages/admin/tenant-role/Create.vue')
        },
        {
            path: '/:tenantId/configuration/profiles/:id/edit',
            name: 'configuration.profile.edit',
            component: () => import('@/pages/admin/tenant-role/Edit.vue')
        },

        {
            path: '/:tenantId/configuration/nichera',
            name: 'configuration.nichera',
            component: () => import('@/pages/admin/configuration/KartioEdit.vue')
        },


        {
            path: '/:tenantId/configuration/store-locator',
            name: 'configuration.store-locator',
            component: () => import('@/pages/admin/configuration/StoreLocatorEdit.vue')
        },

        // Reply Task Assignment by Sender Address

        // Locale configuration
        {
            path: '/:tenantId/locale-configuration',
            name: 'locale-configuration',
            component: () => import('@/pages/admin/locale-configuration/Edit.vue')
        },

        {
            path: '/:tenantId/builder/global-settings',
            name: 'builder.global-settings',
            component: () => import('@/pages/admin/builder/EmbeddedSettings.vue'),
            props: {
                title: 'Global settings',
                nuxtPath: '/_settings/global-defaults',
            },
        },

        {
            path: '/:tenantId/builder/themes',
            name: 'builder.themes',
            component: () => import('@/pages/admin/builder/EmbeddedSettings.vue'),
            props: {
                title: 'Themes',
                nuxtPath: '/_settings/themes',
            },
        },

        // Print Handling Charge
        {
            path: '/:tenantId/print-handling-charges',
            name: 'print-handling-charge.index',
            component: () => import('@/pages/admin/print-handling-charge/Index.vue')
        },
        {
            path: '/:tenantId/print-handling-charges/create',
            name: 'print-handling-charge.create',
            component: () => import('@/pages/admin/print-handling-charge/Create.vue')
        },
        {
            path: '/:tenantId/print-handling-charges/:id/edit',
            name: 'print-handling-charge.edit',
            component: () => import('@/pages/admin/print-handling-charge/Edit.vue')
        },

        // Customer
        {
            path: '/:tenantId/customers',
            name: 'customer.index',
            component: () => import('@/pages/admin/customer/Index.vue')
        },
        {
            path: '/:tenantId/customers/create',
            name: 'customer.create',
            component: () => import('@/pages/admin/customer/Create.vue')
        },
        {
            path: '/:tenantId/customers/:id/edit',
            name: 'customer.edit',
            component: () => import('@/pages/admin/customer/Edit.vue')
        },
        {
            path: '/:tenantId/customers/import',
            name: 'customer.import',
            component: () => import('@/pages/admin/customer/Import.vue')
        },

        // Ticket
        {
            path: '/:tenantId/tickets',
            name: 'ticket.index',
            component: () => import('@/pages/admin/ticket/Index.vue'),
            meta: { module: 'tickets' }
        },
        {
            path: '/:tenantId/tickets/create',
            name: 'ticket.create',
            component: () => import('@/pages/admin/ticket/Create.vue'),
            meta: { module: 'tickets' }
        },
        {
            path: '/:tenantId/tickets/:id/edit',
            name: 'ticket.edit',
            component: () => import('@/pages/admin/ticket/Edit.vue'),
            meta: { module: 'tickets' }
        },
        {
            path: '/:tenantId/contracts',
            name: 'contract.index',
            component: () => import('@/pages/admin/contract/Index.vue'),
            meta: { module: 'tickets' }
        },
        {
            path: '/:tenantId/contracts/create',
            name: 'contract.create',
            component: () => import('@/pages/admin/contract/Create.vue'),
            meta: { module: 'tickets' }
        },
        {
            path: '/:tenantId/contracts/:id/edit',
            name: 'contract.edit',
            component: () => import('@/pages/admin/contract/Edit.vue'),
            meta: { module: 'tickets' }
        },
        // Notification
        {
            path: '/:tenantId/notifications',
            name: 'notification.index',
            component: () => import('@/pages/admin/notification/Index.vue')
        },
        {
            path: '/:tenantId/notifications/:id/edit',
            name: 'notification.edit',
            component: () => import('@/pages/admin/notification/Edit.vue')
        },

        // Currency Rate
        {
            path: '/:tenantId/currency-rates',
            name: 'currency-rate.index',
            component: () => import('@/pages/admin/currency-rate/Index.vue')
        },
        {
            path: '/:tenantId/currency-rates/:id/edit',
            name: 'currency-rate.edit',
            component: () => import('@/pages/admin/currency-rate/Edit.vue')
        },

        // Attachment
        {
            path: '/:tenantId/attachments',
            name: 'attachment.index',
            component: () => import('@/pages/admin/attachment/Index.vue')
        },
        {
            path: '/:tenantId/attachments/create',
            name: 'attachment.create',
            component: () => import('@/pages/admin/attachment/Create.vue')
        },
        {
            path: '/:tenantId/attachments/:id/edit',
            name: 'attachment.edit',
            component: () => import('@/pages/admin/attachment/Edit.vue')
        },

        // Product
        {
            path: '/:tenantId/products',
            name: 'product.index',
            component: () => import('@/pages/admin/product/Index.vue')
        },
        {
            path: '/:tenantId/products/create',
            name: 'product.create',
            component: () => import('@/pages/admin/product/Create.vue')
        },
        {
            path: '/:tenantId/products/:id/edit',
            name: 'product.edit',
            component: () => import('@/pages/admin/product/Edit.vue')
        },

        // Product types
        {
            path: '/:tenantId/product-types',
            name: 'product-type.index',
            component: () => import('@/pages/admin/product-type/Index.vue')
        },
        {
            path: '/:tenantId/product-types/create',
            name: 'product-type.create',
            component: () => import('@/pages/admin/product-type/Create.vue')
        },
        {
            path: '/:tenantId/product-types/:id/edit',
            name: 'product-type.edit',
            component: () => import('@/pages/admin/product-type/Edit.vue')
        },

        // Product attribute
        {
            path: '/:tenantId/product-attributes',
            name: 'product-attribute.index',
            component: () => import('@/pages/admin/product-attribute/Index.vue')
        },
        {
            path: '/:tenantId/product-attributes/create',
            name: 'product-attribute.create',
            component: () => import('@/pages/admin/product-attribute/Create.vue')
        },
        {
            path: '/:tenantId/product-attributes/:id/edit',
            name: 'product-attribute.edit',
            component: () => import('@/pages/admin/product-attribute/Edit.vue')
        },

        // Courier
        {
            path: '/:tenantId/couriers',
            name: 'courier.index',
            component: () => import('@/pages/admin/courier/Index.vue')
        },
        {
            path: '/:tenantId/couriers/create',
            name: 'courier.create',
            component: () => import('@/pages/admin/courier/Create.vue')
        },
        {
            path: '/:tenantId/couriers/:id/edit',
            name: 'courier.edit',
            component: () => import('@/pages/admin/courier/Edit.vue')
        },

        // Shipping
        {
            path: '/:tenantId/shipping',
            name: 'shipping.index',
            component: () => import('@/pages/admin/shipping/Index.vue')
        },
        {
            path: '/:tenantId/shipping/create',
            name: 'shipping.create',
            component: () => import('@/pages/admin/shipping/Create.vue')
        },
        {
            path: '/:tenantId/shipping/:id/edit',
            name: 'shipping.edit',
            component: () => import('@/pages/admin/shipping/Edit.vue')
        },

        // Task
        {
            path: '/:tenantId/tasks',
            name: 'task.index',
            component: () => import('@/pages/admin/task/Index.vue')
        },

        // REST API User
        {
            path: '/:tenantId/rest-api-users',
            name: 'rest-api-user.index',
            component: () => import('@/pages/admin/rest-api-user/Index.vue')
        },
        {
            path: '/:tenantId/rest-api-users/create',
            name: 'rest-api-user.create',
            component: () => import('@/pages/admin/rest-api-user/Create.vue')
        },
        {
            path: '/:tenantId/rest-api-users/:id/edit',
            name: 'rest-api-user.edit',
            component: () => import('@/pages/admin/rest-api-user/Edit.vue')
        },

        // Order
        {
            path: '/:tenantId/orders',
            name: 'order.index',
            component: () => import('@/pages/admin/order/Index.vue')
        },
        {
            path: '/:tenantId/orders/:id/edit',
            name: 'order.edit',
            component: () => import('@/pages/admin/order/Edit.vue')
        },

        // Supplier Control Panel
        {
            path: '/:tenantId/supplier-control-panel',
            name: 'supplier-control-panel.index',
            component: () => import('@/pages/admin/supplier-control-panel/SettingIndex.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/settings/:id/edit',
            name: 'supplier-control-panel.edit-setting',
            component: () => import('@/pages/admin/supplier-control-panel/SettingEdit.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/general-profit/:id/edit',
            name: 'supplier-control-panel.edit-general-profit',
            component: () => import('@/pages/admin/supplier-control-panel/GeneralProfitEdit.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/category-profit/:id/edit',
            name: 'supplier-control-panel.edit-category-profit',
            component: () => import('@/pages/admin/supplier-control-panel/CategoryProfitEdit.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/product-profit/:id/edit',
            name: 'supplier-control-panel.edit-product-profit',
            component: () => import('@/pages/admin/supplier-control-panel/ProductProfitEdit.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/product-translator',
            name: 'supplier-control-panel.product-translator',
            component: () => import('@/pages/admin/supplier-control-panel/ProductTranslatorIndex.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/product-translator/:id/edit',
            name: 'supplier-control-panel.product-translator.edit',
            component: () => import('@/pages/admin/supplier-control-panel/ProductTranslatorEdit.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/grouped-translator/:id/edit',
            name: 'supplier-control-panel.edit-grouped-translator',
            component: () => import('@/pages/admin/supplier-control-panel/GroupedTranslatorEdit.vue')
        },
        {
            path: '/:tenantId/supplier-control-panel/product-shared-translator/:id/edit',
            name: 'supplier-control-panel.edit-product-shared-translator',
            component: () => import('@/pages/admin/supplier-control-panel/ProductSharedTranslatorEdit.vue')
        },

        // Public Tokens
        {
            path: '/:tenantId/public-tokens',
            name: 'public-token.edit',
            component: () => import('@/pages/admin/public-token/Edit.vue')
        },

        // Calcura
        {
            path: '/:tenantId/calcura/calculators',
            name: 'calcura.calculator.index',
            component: () => import('@/pages/admin/calcura-calculator/Index.vue')
        },
        {
            path: '/:tenantId/calcura/calculators/create',
            name: 'calcura.calculator.create',
            component: () => import('@/pages/admin/calcura-calculator/Create.vue')
        },
        {
            path: '/:tenantId/calcura/calculators/:id/edit',
            name: 'calcura.calculator.edit',
            component: () => import('@/pages/admin/calcura-calculator/Edit.vue')
        },

        {
            path: '/:tenantId/calcura/orders',
            name: 'calcura.order.index',
            component: () => import('@/pages/admin/calcura-order/Index.vue')
        },

        {
            path: '/:tenantId/calcura/orders/:id/edit',
            name: 'calcura.order.edit',
            component: () => import('@/pages/admin/calcura-order/Edit.vue')
        },

        // Reply Tasks

        // TamePulse
        {
            path: '/:tenantId/tamepulse/evaluations',
            name: 'tamepulse.evaluation.index',
            component: () => import('@/pages/admin/tamepulse/evaluations/Index.vue')
        },
        {
            path: '/:tenantId/tamepulse/evaluations/create',
            name: 'tamepulse.evaluation.create',
            component: () => import('@/pages/admin/tamepulse/evaluations/Create.vue')
        },
        {
            path: '/:tenantId/tamepulse/evaluations/:id',
            name: 'tamepulse.evaluation.suite',
            component: () => import('@/pages/admin/tamepulse/evaluations/Suite.vue')
        },
        {
            path: '/:tenantId/tamepulse/evaluation-runs/:id',
            name: 'tamepulse.evaluation.run',
            component: () => import('@/pages/admin/tamepulse/evaluations/Run.vue')
        },
        {
            path: '/:tenantId/tamepulse/solutions',
            name: 'tamepulse.solution.index',
            component: () => import('@/pages/admin/tamepulse/solutions/Index.vue')
        },
        {
            path: '/:tenantId/tamepulse/solutions/create/:solutionKey',
            name: 'tamepulse.solution.create',
            component: () => import('@/pages/admin/tamepulse/solutions/Edit.vue')
        },
        {
            path: '/:tenantId/tamepulse/solutions/:id',
            name: 'tamepulse.solution.edit',
            component: () => import('@/pages/admin/tamepulse/solutions/Edit.vue')
        },
        {
            path: '/:tenantId/tamepulse/procedures',
            name: 'tamepulse.procedure.index',
            component: () => import('@/pages/admin/tamepulse/procedures/Index.vue')
        },
        {
            path: '/:tenantId/tamepulse/procedures/create',
            name: 'tamepulse.procedure.create',
            component: () => import('@/pages/admin/tamepulse/procedures/Edit.vue')
        },
        {
            path: '/:tenantId/tamepulse/procedures/:id',
            name: 'tamepulse.procedure.edit',
            component: () => import('@/pages/admin/tamepulse/procedures/Edit.vue')
        },
        {
            path: '/:tenantId/tamepulse/settings',
            name: 'tamepulse.settings.index',
            component: () => import('@/pages/admin/tamepulse/settings/Index.vue')
        },
        {
            path: '/:tenantId/tamepulse/settings/assistance/chat',
            name: 'tamepulse.settings.assistance.chat',
            redirect: to => ({ name: 'tamepulse.solution.index', params: { tenantId: to.params.tenantId } })
        },
        {
            path: '/:tenantId/tamepulse/settings/assistance/email',
            name: 'tamepulse.settings.assistance.email',
            redirect: to => ({ name: 'tamepulse.solution.index', params: { tenantId: to.params.tenantId } })
        },
        {
            path: '/:tenantId/tamepulse/settings/payment-methods',
            name: 'entity.payment-method.index',
            component: () => import('@/pages/admin/entity-payment-method/Index.vue')
        },
        {
            path: '/:tenantId/tamepulse/settings/environments',
            name: 'tamepulse.environment.index',
            component: () => import('@/pages/admin/tamepulse/environment/Index.vue')
        },
        {
            path: '/:tenantId/tamepulse/settings/environments/create',
            name: 'tamepulse.environment.create',
            component: () => import('@/pages/admin/tamepulse/environment/Create.vue')
        },
        {
            path: '/:tenantId/tamepulse/settings/environments/:id/edit',
            name: 'tamepulse.environment.edit',
            component: () => import('@/pages/admin/tamepulse/environment/Edit.vue')
        },
        {
            path: '/:tenantId/tamepulse/connections',
            name: 'tamepulse.connection',
            component: () => import('@/pages/admin/tamepulse/connections/Index.vue'),
            meta: { module: 'connections' }
        },
        {
            path: '/:tenantId/tamepulse/connections/create',
            name: 'tamepulse.connection.create',
            component: () => import('@/pages/admin/tamepulse/connections/Create.vue'),
            meta: { module: 'connections' }
        },
        {
            path: '/:tenantId/tamepulse/connections/:id/edit',
            name: 'tamepulse.connection.edit',
            component: () => import('@/pages/admin/tamepulse/connections/Edit.vue'),
            meta: { module: 'connections' }
        },
        {
            path: '/:tenantId/tamepulse/chat',
            name: 'tamepulse.chat.index',
            component: () => import('@/pages/admin/tamepulse/chat/Index.vue'),
            meta: { module: 'chat' }
        },
        {
            path: '/:tenantId/tamepulse/conversations',
            name: 'tamepulse.inbox.index',
            component: () => import('@/pages/admin/tamepulse/inbox/Index.vue'),
            meta: { module: 'chat' }
        },
        {
            path: '/:tenantId/tamepulse/visitor-chat',
            name: 'tamepulse.visitorChat.index',
            component: () => import('@/pages/admin/tamepulse/visitor-chat/Index.vue'),
            meta: { module: 'chat' }
        },
        {
            path: '/:tenantId/tamepulse/knowledge-base',
            name: 'tamepulse.knowledgeBase.index',
            component: () => import('@/pages/admin/tamepulse/knowledge-base/Index.vue'),
            meta: { module: 'knowledge_base' }
        },
        {
            path: '/:tenantId/tamepulse/support/mailboxes',
            name: 'tamepulse.support.mailboxes',
            component: () => import('@/pages/admin/tamepulse/mail/Index.vue'),
            props: { configurationOnly: true },
            meta: { module: 'chat' }
        },
        {
            path: '/:tenantId/tamepulse/mail',
            name: 'tamepulse.mail.index',
            component: () => import('@/pages/admin/tamepulse/mail/Index.vue'),
            meta: { module: 'mail' }
        },
        {
            path: '/:tenantId/tamepulse/invoices',
            name: 'tamepulse.invoice.index',
            component: () => import('@/pages/admin/tamepulse/invoice/Index.vue'),
            meta: { module: 'invoices' }
        },
        {
            path: '/:tenantId/tamepulse/invoices/create',
            name: 'tamepulse.invoice.create',
            component: () => import('@/pages/admin/tamepulse/invoice/Create.vue'),
            meta: { module: 'invoices' }
        },
        {
            path: '/:tenantId/tamepulse/invoices/settings',
            name: 'tamepulse.invoice.settings',
            component: () => import('@/pages/admin/tamepulse/invoice/Settings.vue'),
            meta: { module: 'invoices' }
        },
        {
            path: '/:tenantId/tamepulse/invoices/:id/edit',
            name: 'tamepulse.invoice.edit',
            component: () => import('@/pages/admin/tamepulse/invoice/Edit.vue'),
            meta: { module: 'invoices' }
        },
        {
            path: '/:tenantId/tamepulse/playbooks',
            name: 'tamepulse.playbook.index',
            component: () => import('@/pages/admin/tamepulse/playbook/Index.vue'),
            meta: { module: 'playbooks' }
        },
        {
            path: '/:tenantId/tamepulse/playbooks/create',
            name: 'tamepulse.playbook.create',
            component: () => import('@/pages/admin/tamepulse/playbook/Create.vue'),
            meta: { module: 'playbooks' }
        },
        {
            path: '/:tenantId/tamepulse/playbooks/:id/edit',
            name: 'tamepulse.playbook.edit',
            component: () => import('@/pages/admin/tamepulse/playbook/Edit.vue'),
            meta: { module: 'playbooks' }
        },
        {
            path: '/:tenantId/tamepulse/playbooks/:id/runs',
            name: 'tamepulse.playbook.runs',
            component: () => import('@/pages/admin/tamepulse/playbook/RunHistory.vue'),
            meta: { module: 'playbooks' }
        },
        {
            path: '/:tenantId/tamepulse/templates',
            name: 'tamepulse.template.index',
            component: () => import('@/pages/admin/tamepulse/templates/Index.vue'),
            meta: { module: 'templates' }
        },
        {
            path: '/:tenantId/tamepulse/approvals',
            name: 'tamepulse.approval.index',
            component: () => import('@/pages/admin/tamepulse/approval/Index.vue'),
            meta: { module: 'approvals' }
        },
        {
            path: '/:tenantId/tamepulse/approvals/:id',
            name: 'tamepulse.approval.show',
            component: () => import('@/pages/admin/tamepulse/approval/Show.vue'),
            meta: { module: 'approvals' }
        },
        {
            path: '/:tenantId/tamepulse/skills',
            name: 'tamepulse.skill.index',
            component: () => import('@/pages/admin/tamepulse/skill/Index.vue'),
            meta: { module: 'skills' }
        },
        {
            path: '/:tenantId/tamepulse/skills/create',
            name: 'tamepulse.skill.create',
            component: () => import('@/pages/admin/tamepulse/skill/Create.vue'),
            meta: { module: 'skills' }
        },
        {
            path: '/:tenantId/tamepulse/skills/:id/edit',
            name: 'tamepulse.skill.edit',
            component: () => import('@/pages/admin/tamepulse/skill/Edit.vue'),
            meta: { module: 'skills' }
        },
        {
            path: '/:tenantId/tamepulse/messages',
            name: 'tamepulse.message.index',
            component: () => import('@/pages/admin/tamepulse/message/Index.vue'),
            meta: { module: 'messages' }
        },
        {
            path: '/:tenantId/tamepulse/agents',
            name: 'tamepulse.agent.index',
            component: () => import('@/pages/admin/tamepulse/agent/Index.vue'),
            meta: { module: 'agents' }
        },
        {
            path: '/:tenantId/tamepulse/agents/create',
            name: 'tamepulse.agent.create',
            component: () => import('@/pages/admin/tamepulse/agent/Create.vue'),
            meta: { module: 'agents' }
        },
        {
            path: '/:tenantId/tamepulse/agents/:id/edit',
            name: 'tamepulse.agent.edit',
            component: () => import('@/pages/admin/tamepulse/agent/Edit.vue'),
            meta: { module: 'agents' }
        },
        {
            path: '/:tenantId/tamepulse/agent-profiles',
            name: 'tamepulse.agentProfile.index',
            component: () => import('@/pages/admin/tamepulse/agent-profile/Index.vue'),
            meta: { module: 'agents' }
        },
        {
            path: '/:tenantId/tamepulse/agent-profiles/create',
            name: 'tamepulse.agentProfile.create',
            component: () => import('@/pages/admin/tamepulse/agent-profile/Create.vue'),
            meta: { module: 'agents' }
        },
        {
            path: '/:tenantId/tamepulse/agent-profiles/:id/edit',
            name: 'tamepulse.agentProfile.edit',
            component: () => import('@/pages/admin/tamepulse/agent-profile/Edit.vue'),
            meta: { module: 'agents' }
        },

        // Store Locator
        {
            path: '/:tenantId/stores',
            name: 'store.index',
            component: () => import('@/pages/admin/store/Index.vue')
        },
        {
            path: '/:tenantId/stores/import',
            name: 'store.import',
            component: () => import('@/pages/admin/store/Import.vue')
        },

        // Menus
        {
            path: '/:tenantId/menus',
            name: 'menu.index',
            component: () => import('@/pages/admin/menu/Index.vue')
        },
        {
            path: '/:tenantId/menus/create',
            name: 'menu.create',
            component: () => import('@/pages/admin/menu/Create.vue')
        },
        {
            path: '/:tenantId/menus/:id/edit',
            name: 'menu.edit',
            component: () => import('@/pages/admin/menu/Edit.vue')
        },

        // Slotflow
        {
            path: '/:tenantId/slotflow/forms',
            name: 'slotflow.form.index',
            component: () => import('@/pages/admin/slotflow/form/Index.vue')
        },
        {
            path: '/:tenantId/slotflow/forms/create',
            name: 'slotflow.form.create',
            component: () => import('@/pages/admin/slotflow/form/Create.vue')
        },
        {
            path: '/:tenantId/slotflow/forms/:id/edit',
            name: 'slotflow.form.edit',
            component: () => import('@/pages/admin/slotflow/form/Edit.vue')
        },
        {
            path: '/:tenantId/slotflow/calendar-connections',
            name: 'slotflow.calendar-connection.index',
            component: () => import('@/pages/admin/slotflow/calendar-connection/Index.vue')
        },
        {
            path: '/:tenantId/slotflow/calendar-connections/create',
            name: 'slotflow.calendar-connection.create',
            component: () => import('@/pages/admin/slotflow/calendar-connection/Create.vue')
        },
        {
            path: '/:tenantId/slotflow/calendar-connections/:id/edit',
            name: 'slotflow.calendar-connection.edit',
            component: () => import('@/pages/admin/slotflow/calendar-connection/Edit.vue')
        },
        {
            path: '/:tenantId/slotflow/bookings',
            name: 'slotflow.booking.index',
            component: () => import('@/pages/admin/slotflow/booking/Index.vue')
        },
        {
            path: '/:tenantId/slotflow/submissions',
            name: 'slotflow.submission.index',
            component: () => import('@/pages/admin/slotflow/submission/Index.vue')
        },

        // Lead Capture
        {
            path: '/:tenantId/lead-capture/forms',
            name: 'lead-capture.form.index',
            component: () => import('@/pages/admin/lead-capture/form/Index.vue')
        },
        {
            path: '/:tenantId/lead-capture/forms/create',
            name: 'lead-capture.form.create',
            component: () => import('@/pages/admin/lead-capture/form/Create.vue')
        },
        {
            path: '/:tenantId/lead-capture/forms/:id/edit',
            name: 'lead-capture.form.edit',
            component: () => import('@/pages/admin/lead-capture/form/Edit.vue')
        },
        {
            path: '/:tenantId/lead-capture/submissions',
            name: 'lead-capture.submission.index',
            component: () => import('@/pages/admin/lead-capture/submission/Index.vue')
        },

        // Review Widget
        {
            path: '/:tenantId/review-widget/forms',
            name: 'review-widget.form.index',
            component: () => import('@/pages/admin/review-widget/form/Index.vue')
        },
        {
            path: '/:tenantId/review-widget/forms/create',
            name: 'review-widget.form.create',
            component: () => import('@/pages/admin/review-widget/form/Create.vue')
        },
        {
            path: '/:tenantId/review-widget/forms/:id/edit',
            name: 'review-widget.form.edit',
            component: () => import('@/pages/admin/review-widget/form/Edit.vue')
        },
        {
            path: '/:tenantId/review-widget/reviews',
            name: 'review-widget.review.index',
            component: () => import('@/pages/admin/review-widget/review/Index.vue')
        },

        ...entityRoutes,
        ...legacyEntityFieldRoutes,
        ...appEntityRoutes,
        appEntityNotFoundRoute,

        // 404 Not Found
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            meta: { layoutless: true },
            component: () => import('@/pages/NotFound.vue')
        }
    ],
    scrollBehavior(to, from, savedPosition) {
        const preventScroll = window.__smartRouterPreventScroll || window.history.state?.preventScroll
        if (preventScroll) return false
        if (savedPosition) return savedPosition
        if (to.hash) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        el: to.hash,
                        behavior: 'smooth',
                    })
                }, 250)
            })
        }
        return { top: 0 }
    }
})

router.beforeEach(async (to, from, next) => {
    if (isEntityRoute(to) && VueCookies.get('admin_token')) {
        let definitions = await fetchRouteEntityDefinitions(resolveTenantId(to))
        if (to.meta?.appEntity) {
            let definition = findAppEntityDefinition(definitions, to.params)
            if (!definition) {
                definitions = await fetchRouteEntityDefinitions(resolveTenantId(to), { refresh: true })
                definition = findAppEntityDefinition(definitions, to.params)
            }
            if (!definition) {
                next(missingAppEntityRoute(to))
                return
            }
            to.params.entityType = definition.slug
            to.meta.appEntityDefinition = definition
        } else {
            const definition = definitions.find(item => item.slug === to.params.entityType)
            const target = entityRouteForDefinition(to, definition)
            if (target !== to) {
                next({ ...target, replace: true })
                return
            }
        }
    }

    previousRoute.value['original'] = from

    // Mantieni logica di previousRoute se necessaria
    if (!from.meta?.hasAction) {
        previousRoute.value['index'] = from
    }

    if (from.name === 'supplier-control-panel.product-translator') {
        previousRoute.value['productTranslatorIndex'] = from
    }

    if (to.name === 'tamepulse.chat.index' || String(to.name || '').startsWith('apps.')) {
        const tenant = await fetchRouteTenantConfiguration(resolveTenantId(to))
        const enabled = to.name === 'tamepulse.chat.index' ? tenant?.internal_chat_enabled : tenant?.apps_enabled
        if (enabled !== true) {
            next({ name: 'dashboard.index', params: { tenantId: resolveTenantId(to) }, replace: true })
            return
        }
    }

    if (to.name === 'entity.payment-method.index' && VueCookies.get('admin_token')) {
        if (!await paymentMethodSettingsAvailable(resolveTenantId(to))) {
            next({ name: 'tamepulse.settings.index', params: { tenantId: resolveTenantId(to) }, replace: true })
            return
        }
    }

    if (to.name !== 'admin.module-unavailable' && to.meta?.public !== true && await routeModuleInactive(to, to.meta?.module)) {
        next({
            name: 'admin.module-unavailable',
            params: { tenantId: resolveTenantId(to) },
            query: { module: to.meta.module },
        })
        return
    }

    if (to.name === 'admin.module-unavailable' && !await routeModuleInactive(to, to.query?.module, { refresh: true })) {
        next({ name: 'dashboard.index', params: { tenantId: resolveTenantId(to) } })
        return
    }

    next()
})

async function routeModuleInactive(route, moduleKey, options = {}) {
    const normalizedModuleKey = normalizeTenantModuleKey(moduleKey)

    if (!normalizedModuleKey) {
        return false
    }

    const tenantId = resolveTenantId(route)

    if (!tenantId || ['login', 'auth'].includes(String(tenantId))) {
        return false
    }

    const cachedActiveModules = options.refresh ? null : cachedTenantActiveModules(tenantId)

    if (Array.isArray(cachedActiveModules)) {
        return !cachedActiveModules.includes(normalizedModuleKey)
    }

    const activeModules = await fetchActiveModules(tenantId)

    if (!Array.isArray(activeModules)) {
        return false
    }

    return !activeModules.includes(normalizedModuleKey)
}

async function paymentMethodSettingsAvailable(tenantId) {
    try {
        const token = VueCookies.get('admin_token')
        const response = await fetch(`${import.meta.env.VITE_BASE_URL}/${tenantId}/graphql`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Locale': 'en',
            },
            body: JSON.stringify({ query: 'query RoutePaymentMethodSettingsAvailability { entityPaymentMethodSettingsAvailable }' }),
        })
        if (!response.ok) return false
        const payload = await response.json()
        return !payload.errors?.length && payload.data?.entityPaymentMethodSettingsAvailable === true
    } catch {
        return false
    }
}

async function fetchActiveModules(tenantId) {
    const tenant = await fetchRouteTenantConfiguration(tenantId)
    return Array.isArray(tenant?.active_modules) ? tenant.active_modules : null
}

async function fetchRouteTenantConfiguration(tenantId) {
    try {
        const token = VueCookies.get('admin_token')
        const response = await fetch(`${import.meta.env.VITE_BASE_URL}/${tenantId}/graphql`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                'X-Locale': 'en',
                'Accept-Language': 'en',
            },
            body: JSON.stringify({ query: ROUTE_TENANT_MODULES_QUERY }),
        })

        if (!response.ok) {
            return null
        }

        const payload = await response.json()

        const tenant = payload?.data?.currentTenant
        rememberTenantOfferSetup(tenantId, tenant?.offer_setup_required)
        if (Array.isArray(tenant?.active_modules)) {
            tenant.active_modules = rememberTenantActiveModules(tenantId, tenant.active_modules)
        }
        return tenant ?? null
    } catch (error) {
        console.error(error)
        return null
    }
}

function resolveTenantId(route) {
    return route.params?.tenantId
}

const routeEntityDefinitions = new Map()

async function fetchRouteEntityDefinitions(tenantId, { refresh = false } = {}) {
    const token = VueCookies.get('admin_token')
    const cached = routeEntityDefinitions.get(tenantId)
    if (!refresh && cached?.token === token && cached.expiresAt > Date.now()) return cached.definitions

    try {
        const response = await fetch(`${import.meta.env.VITE_BASE_URL}/${encodeURIComponent(tenantId)}/graphql`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ query: 'query RouteEntityDefinitions { entityTypeDefinitions { slug app_catalog } }' }),
        })
        if (!response.ok) return []
        const payload = await response.json()
        const definitions = payload?.data?.entityTypeDefinitions
        if (!Array.isArray(definitions)) return []
        routeEntityDefinitions.set(tenantId, { token, definitions, expiresAt: Date.now() + 30_000 })
        return definitions
    } catch (error) {
        console.error(error)
        return []
    }
}
